import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";

const Responsibilities = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Your Responsibilities" 
        description="Vehicle, insurance, and equipment requirements"
      >
        <section className="space-y-8">
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Vehicle Requirements</h2>
            <p className="text-muted-foreground mb-4">
              You are responsible for providing reliable transportation to customer sites.
            </p>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-3">Option 1: Use Your Own Vehicle</h3>
                <p className="text-muted-foreground mb-2">
                  If you use your own vehicle, BlueLux will pay a <strong>$1.20 per pool mobility stipend</strong> to 
                  offset fuel and basic vehicle costs.
                </p>
                <div className="bg-card border border-border rounded-lg p-4 mt-3">
                  <p className="font-medium text-foreground mb-2">Your vehicle must meet these standards:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                    <li>Reliable and properly maintained</li>
                    <li>Sufficient cargo capacity for pool equipment and chemicals</li>
                    <li>Professional appearance (clean, no major damage)</li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-3">Option 2: Lease a BlueLux Vehicle</h3>
                <p className="text-muted-foreground mb-2">
                  If you elect to use a vehicle provided by BlueLux, you agree to a <strong>$15 per day vehicle 
                  lease fee</strong> (based on an assumed route of 100 pools per week).
                </p>
                <div className="bg-card border border-border rounded-lg p-4 mt-3">
                  <p className="font-medium text-foreground mb-2">This fee covers:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                    <li>Vehicle lease costs</li>
                    <li>Insurance coverage</li>
                    <li>Basic maintenance</li>
                  </ul>
                  <p className="text-sm text-muted-foreground mt-3 italic">
                    BlueLux-provided vehicles are for business use only and must be maintained in clean, 
                    professional condition.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Insurance Requirements</h2>
            
            <div className="space-y-6">
              <div className="bg-card border-2 border-primary rounded-lg p-6">
                <h3 className="text-lg font-semibold text-primary mb-3">Automobile Liability Insurance (Required)</h3>
                <p className="text-muted-foreground mb-3">
                  You must maintain automobile liability insurance with minimum coverage of:
                </p>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  <li><strong>$100,000</strong> per person</li>
                  <li><strong>$300,000</strong> per accident</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-3">General Liability Coverage</h3>
                <p className="text-muted-foreground mb-3">
                  BlueLux may, at its discretion, maintain a master liability policy covering contractors while 
                  on active BlueLux jobs.
                </p>
                <p className="text-muted-foreground">
                  If no such policy is in place, you must maintain your own general liability coverage.
                </p>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-3">Workers' Compensation</h3>
                <p className="text-muted-foreground">
                  Workers' compensation insurance is only required if you employ others. As an independent 
                  contractor working alone, you are not covered by BlueLux's workers' compensation policy.
                </p>
              </div>

              <div className="bg-muted/50 border border-border rounded-lg p-4">
                <p className="text-sm font-medium text-foreground mb-2">Insurance Documentation</p>
                <p className="text-sm text-muted-foreground">
                  You may be required to provide proof of insurance coverage and maintain current documentation 
                  on file with BlueLux.
                </p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Tools & Equipment</h2>
            
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium mb-3">What BlueLux Provides</h3>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  <li><strong>Pool chemicals</strong> (covered by $5 service fee deduction)</li>
                  <li>Platform support and administrative tools</li>
                  <li>Optional equipment purchase/lease at fair market value</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-3">What You Must Provide</h3>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground">
                  <li>Water test kit</li>
                  <li>Smartphone capable of running the BlueLux Technician App</li>
                  <li>Personal protective equipment (PPE):
                    <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                      <li>Chemical-resistant gloves</li>
                      <li>Safety goggles</li>
                      <li>Closed-toe shoes</li>
                    </ul>
                  </li>
                  <li>Basic pool maintenance tools (or purchase/lease from BlueLux)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium mb-3">Equipment Purchase/Lease Options</h3>
                <p className="text-muted-foreground">
                  You may provide your own tools and equipment, or purchase/rent them from BlueLux at fair market value.
                </p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Business Licenses & Permits</h2>
            <p className="text-muted-foreground mb-3">
              You are responsible for obtaining and maintaining any permits or licenses required to perform pool 
              service in your jurisdiction.
            </p>
            <p className="text-muted-foreground">
              This may include business licenses, contractor licenses, or other local permits. Check with your 
              local authorities for specific requirements.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Maintenance Responsibilities</h2>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium">If Using Your Own Vehicle</h3>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground mt-2">
                  <li>Regular maintenance (oil changes, tire rotations, etc.)</li>
                  <li>Repairs as needed</li>
                  <li>Cleaning and professional appearance</li>
                  <li>Valid registration and inspection</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium">If Using a BlueLux Vehicle</h3>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground mt-2">
                  <li>Daily cleaning and upkeep</li>
                  <li>Reporting any damage or mechanical issues immediately</li>
                  <li>Using vehicle only for BlueLux business</li>
                  <li>Following all BlueLux vehicle policies</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-medium">Equipment Care</h3>
                <ul className="list-disc list-inside space-y-2 text-muted-foreground mt-2">
                  <li>Clean and maintain all tools and equipment</li>
                  <li>Properly store chemicals according to safety guidelines</li>
                  <li>Report damaged or worn equipment</li>
                  <li>Replace worn or broken tools as needed</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-muted/50 border border-border rounded-lg p-6 mt-8">
            <h3 className="text-lg font-medium mb-3">Safety First</h3>
            <p className="text-sm text-muted-foreground mb-3">
              Maintaining your vehicle, insurance, and equipment in good working order is not just a requirement—it's 
              essential for your safety, the safety of others, and the quality of service you provide to BlueLux customers.
            </p>
            <p className="text-sm text-muted-foreground">
              Report any safety concerns, equipment failures, or insurance lapses immediately to BlueLux management.
            </p>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default Responsibilities;
